<template>
  <teleport to="body">
    <template v-if="show">
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        leave-active-class="transition-opacity duration-250 ease-in"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          class="panel-modal-backdrop fixed inset-0"
          :class="backdropZIndexClass"
          dusk="modal-backdrop"
        />
      </Transition>

      <div
        v-bind="defaultAttributes"
        class="modal fixed inset-0"
        :class="[
          modalZIndexClass,
          {
          'flex items-start justify-center p-4 overflow-y-auto':
            modalStyle === 'window',
          'h-full': modalStyle === 'fullscreen',
        }]"
        :role="role"
        :data-modal-open="show"
        :aria-modal="show"
      >
        <Transition
          appear
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
          enter-to-class="opacity-100 translate-y-0 scale-100"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="opacity-100 translate-y-0 scale-100"
          leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
        >
          <div
            class="@container/modal relative z-20 w-full"
            :class="contentClasses"
            ref="modalContent"
          >
            <slot />
          </div>
        </Transition>
      </div>
    </template>
  </teleport>
</template>

<script setup>
import { useStore } from 'vuex'
import filter from 'lodash/filter'
import omit from 'lodash/omit'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { useEventListener } from '@vueuse/core'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useAttrs,
  watch,
} from 'vue'

const modalContent = ref(null)
const activateFocusTrap = ref(() => {})
const deactivateFocusTrap = ref(() => {})

const attrs = useAttrs()

const emit = defineEmits(['showing', 'closing', 'close-via-escape'])

defineOptions({ inheritAttrs: false })

const props = defineProps({
  show: { type: Boolean, default: false },
  size: {
    type: String,
    default: 'xl',
    validator: v =>
      [
        'sm',
        'md',
        'lg',
        'xl',
        '2xl',
        '3xl',
        '4xl',
        '5xl',
        '6xl',
        '7xl',
      ].includes(v),
  },
  modalStyle: { type: String, default: 'window' },
  role: { type: String, default: 'dialog' },
  useFocusTrap: { type: Boolean, default: true },
  stackPriority: {
    type: String,
    default: 'default',
    validator: v => ['default', 'top'].includes(v),
  },
})

if (props.useFocusTrap) {
  const { activate, deactivate } = useFocusTrap(modalContent, {
    immediate: false,
    allowOutsideClick: false,
    escapeDeactivates: false,
  })

  activateFocusTrap.value = activate
  deactivateFocusTrap.value = deactivate
}

watch(
  () => props.show,
  v => handleVisibilityChange(v),
  { immediate: true }
)

useEventListener(document, 'keydown', e => {
  if (e.key === 'Escape' && props.show === true) {
    emit('close-via-escape', e)
  }
})

onMounted(() => {
  if (props.show === true) handleVisibilityChange(true)
})

onBeforeUnmount(() => {
  document.body.classList.remove('overflow-hidden')
  Nova.resumeShortcuts()
  if (props.useFocusTrap) deactivateFocusTrap.value()
})

const store = useStore()

async function handleVisibilityChange(showing) {
  await nextTick()

  if (showing === true) {
    emit('showing')
    document.body.classList.add('overflow-hidden')
    Nova.pauseShortcuts()
    if (props.useFocusTrap) activateFocusTrap.value()
  } else {
    emit('closing')
    document.body.classList.remove('overflow-hidden')
    Nova.resumeShortcuts()
    if (props.useFocusTrap) deactivateFocusTrap.value()
  }

  store.commit('allowLeavingModal')
}

const defaultAttributes = computed(() => {
  return omit(attrs, ['class'])
})

const sizeClasses = computed(() => {
  return {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
  }
})

const contentClasses = computed(() => {
  let windowClasses = props.modalStyle === 'window' ? sizeClasses.value : {}

  return filter([
    windowClasses[props.size] ?? null,
    // my-auto centers the panel when it fits and collapses when it doesn't,
    // so a taller-than-viewport modal stays scrollable from its top edge.
    props.modalStyle === 'fullscreen' ? 'h-full' : 'my-auto',
    attrs.class,
  ])
})

const backdropZIndexClass = computed(() =>
  props.stackPriority === 'top' ? 'z-[750]' : 'z-[55]'
)

const modalZIndexClass = computed(() =>
  props.stackPriority === 'top' ? 'z-[760]' : 'z-[60]'
)
</script>
